import { FC, ReactNode } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { Autocomplete, AutocompleteProps, FormControlLabel, FormHelperText, TextField } from '@mui/material';

export interface SelectOption {
  value: string | number;
  label?: string;
  disabled?: boolean;
}

export type MultipleSelectProps = Omit<
  AutocompleteProps<SelectOption, true, false, false>,
  'value' | 'onChange' | 'renderInput' | 'multiple'
> & {
  name: string;
  label: ReactNode | string;
  placeholder?: string;
  required?: boolean;
  formatOptionLabel?: (option: SelectOption) => string;
};

const MultipleSelect: FC<MultipleSelectProps> = ({
  name,
  label,
  required = false,
  placeholder,
  formatOptionLabel,
  ...selectProps
}) => {
  const { watch, control, setValue } = useFormContext();

  const fieldValue = watch(name);

  const getOptionLabel = (option: SelectOption) =>
    formatOptionLabel?.(option) || option.label || String(option.value).replace(/_/g, ' ');

  const options = selectProps.options ?? [];
  const selectedOptions = options.filter((option) => fieldValue?.includes(option.value));

  const defaultRules = required ? { required: true } : {};

  return (
    <FormControlLabel
      label={label}
      required={required}
      control={
        <Controller
          name={name}
          control={control}
          rules={defaultRules}
          render={({ field, fieldState }) => (
            <>
              <FormHelperText error={!!fieldState.error}>{fieldState.error?.message as string}</FormHelperText>
              <Autocomplete
                {...field}
                {...selectProps}
                multiple
                options={options}
                value={selectedOptions}
                fullWidth
                isOptionEqualToValue={(option, value) => option.value === value.value}
                getOptionLabel={getOptionLabel}
                onChange={(_, newValue) => {
                  setValue(
                    name,
                    newValue.map((option) => option.value),
                    { shouldDirty: true, shouldValidate: true },
                  );
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder={selectedOptions.length ? undefined : placeholder}
                    error={!!fieldState.error}
                  />
                )}
              />
            </>
          )}
        />
      }
    />
  );
};

export default MultipleSelect;
